import { RefreshCw, RotateCcw } from "lucide-react";
import bgDayUrl from "../assets/bg-day.png";
import bgNightUrl from "../assets/bg-night.png";
import logoUrl from "../assets/logo.png";

function isDaytime(): boolean {
  const h = new Date().getHours() + new Date().getMinutes() / 60;
  const m = new Date().getMonth();
  const dawn = [7.5, 7.0, 6.25, 5.5, 4.75, 4.5, 5.0, 5.5, 6.25, 7.0, 7.5, 7.75][m];
  const dusk = [17.0, 17.75, 18.5, 19.5, 20.25, 21.25, 21.25, 20.75, 19.75, 18.5, 17.25, 16.75][m];
  return h >= dawn && h < dusk;
}

export interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

/**
 * Shown when Sentry.ErrorBoundary catches a render error.
 * Does not surface error.message or stack to end users; optional opaque eventId for support correlation in Sentry.
 */
export function ErrorFallback(props: ErrorFallbackProps) {
  const { eventId, resetError } = props;
  const day = isDaytime();
  const bgImage = day ? bgDayUrl : bgNightUrl;
  const refId = typeof eventId === "string" ? eventId.trim() : "";

  return (
    <div
      className="min-h-[100dvh] w-full flex flex-col items-center justify-center p-6 relative"
      style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-black/50 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-6">
        <img src={logoUrl} alt="Flight Deck" className="w-full max-w-xs drop-shadow-2xl opacity-95" />

        <div className="w-full bg-black/45 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl px-8 py-10 text-center space-y-4">
          <h1 className="text-xl font-semibold text-white tracking-tight">
            Something went wrong
          </h1>
          <p className="text-sm text-white/75 leading-relaxed">
            We hit a snag loading this page. It is usually temporary — try again, or refresh the page.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 pt-2 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black hover:bg-amber-400 transition-colors"
            >
              <RefreshCw className="w-4 h-4 shrink-0" aria-hidden />
              Refresh page
            </button>
            <button
              type="button"
              onClick={() => resetError()}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-medium text-white hover:bg-white/15 transition-colors"
            >
              <RotateCcw className="w-4 h-4 shrink-0" aria-hidden />
              Try again
            </button>
          </div>
        </div>
      </div>

      {refId ? (
        <p
          className="absolute bottom-3 right-4 z-10 max-w-[min(100vw-2rem,20rem)] text-right text-[10px] leading-tight text-white/35 font-mono break-all"
          title="Support reference — use in Sentry to find this event"
        >
          Ref: {refId}
        </p>
      ) : null}
    </div>
  );
}
